// ─────────────────────────────────────────
// AccountModal.js — edit your profile & change password
// ─────────────────────────────────────────
// Slides up as a full-screen sheet.
// Saves to the 'profiles' table in Supabase.
// Password change goes through Supabase Auth.

import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

const EMOJI_OPTIONS = [
  '🏌️', '⛳', '🏆', '🦅', '🔥', '💪', '🎯', '🌿',
  '🏔️', '🌊', '🌄', '⭐', '🎖️', '🤝', '🏅', '🌍',
  '✈️', '🎉', '🏡', '🌲', '🍀', '🎱', '🐦', '👑'
]

const GENDER_OPTIONS = ['Prefer not to say', 'Male', 'Female', 'Non-binary', 'Other']

export default function AccountModal({ session, onClose, onProfileUpdated }) {
  // ── Profile fields ───────────────────────────────────
  const [username, setUsername] = useState('')
  const [fullName, setFullName] = useState('')
  const [bio, setBio] = useState('')
  const [location, setLocation] = useState('')
  const [birthday, setBirthday] = useState('')
  const [gender, setGender] = useState('Prefer not to say')
  const [avatarEmoji, setAvatarEmoji] = useState('🏌️')
  const [avatarUrl, setAvatarUrl] = useState(null)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [handicap, setHandicap] = useState('')
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const photoInputRef = useRef(null)

  // ── Password change ──────────────────────────────────
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPasswordSection, setShowPasswordSection] = useState(false)

  // ── UI state ─────────────────────────────────────────
  const [saving, setSaving] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)
  const [profileMsg, setProfileMsg] = useState(null)   // { type: 'ok'|'err', text }
  const [passwordMsg, setPasswordMsg] = useState(null)
  const [activeTab, setActiveTab] = useState('profile') // 'profile' | 'friends' | 'password'

  // ── Find Friends ─────────────────────────────────────
  const [friendSearch, setFriendSearch] = useState('')
  const [friendResults, setFriendResults] = useState([])
  const [friendSearching, setFriendSearching] = useState(false)
  const [myFollowingIds, setMyFollowingIds] = useState(new Set())
  const [followingMap, setFollowingMap] = useState({}) // id → true/false (optimistic)

  // Load current profile on open
  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('profiles')
        .select('username, full_name, bio, location, birthday, gender, avatar_emoji, avatar_url, handicap')
        .eq('id', session.user.id)
        .single()
      if (data) {
        setUsername(data.username || '')
        setFullName(data.full_name || '')
        setBio(data.bio || '')
        setLocation(data.location || '')
        setBirthday(data.birthday || '')
        setGender(data.gender || 'Prefer not to say')
        setAvatarEmoji(data.avatar_emoji || '🏌️')
        setAvatarUrl(data.avatar_url || null)
        setHandicap(data.handicap != null ? String(data.handicap) : '')
      }
    }
    load()
  }, [session.user.id])

  // Load who I'm following when Friends tab opens
  useEffect(() => {
    if (activeTab !== 'friends') return
    async function loadFollowing() {
      const { data } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', session.user.id)
      const ids = new Set((data || []).map(r => r.following_id))
      setMyFollowingIds(ids)
      const map = {}
      ids.forEach(id => { map[id] = true })
      setFollowingMap(map)
    }
    loadFollowing()
  }, [activeTab, session.user.id])

  // Debounced search
  useEffect(() => {
    if (!friendSearch.trim()) { setFriendResults([]); return }
    const timer = setTimeout(async () => {
      setFriendSearching(true)
      const q = friendSearch.trim()
      const { data } = await supabase
        .from('profiles')
        .select('id, username, full_name, avatar_emoji, avatar_url, handicap, location')
        .or(`username.ilike.%${q}%,full_name.ilike.%${q}%`)
        .neq('id', session.user.id)
        .limit(12)
      setFriendResults(data || [])
      setFriendSearching(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [friendSearch, session.user.id])

  async function handleFollow(profileId) {
    setFollowingMap(prev => ({ ...prev, [profileId]: true }))
    await supabase.from('follows').insert({ follower_id: session.user.id, following_id: profileId })
  }

  async function handleUnfollow(profileId) {
    setFollowingMap(prev => ({ ...prev, [profileId]: false }))
    await supabase.from('follows').delete().eq('follower_id', session.user.id).eq('following_id', profileId)
  }

  // ── Save profile ─────────────────────────────────────
  async function handleSaveProfile() {
    if (!username.trim()) { setProfileMsg({ type: 'err', text: 'Username is required.' }); return }
    setSaving(true)
    setProfileMsg(null)

    const { error } = await supabase
      .from('profiles')
      .upsert({
        id: session.user.id,
        username: username.trim().toLowerCase().replace(/\s/g, ''),
        full_name: fullName.trim() || null,
        bio: bio.trim() || null,
        location: location.trim() || null,
        birthday: birthday || null,
        gender,
        avatar_emoji: avatarEmoji,
        avatar_url: avatarUrl || null,
        handicap: parseFloat(handicap) || null
      }, { onConflict: 'id' })

    if (error) {
      setProfileMsg({ type: 'err', text: error.message })
    } else {
      setProfileMsg({ type: 'ok', text: 'Profile saved!' })
      if (onProfileUpdated) onProfileUpdated({ avatar_emoji: avatarEmoji, avatar_url: avatarUrl, username })
      setTimeout(() => setProfileMsg(null), 2500)
    }
    setSaving(false)
  }

  // ── Avatar photo upload ───────────────────────────────
  async function handleAvatarPhotoChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingAvatar(true)
    setProfileMsg(null)
    const ext = file.name.split('.').pop()
    const path = `${session.user.id}/avatar.${ext}`
    const { error: upErr } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true, contentType: file.type })
    if (upErr) {
      setProfileMsg({ type: 'err', text: 'Photo upload failed: ' + upErr.message })
      setUploadingAvatar(false)
      return
    }
    const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path)
    // Bust cache with a timestamp query param
    setAvatarUrl(publicUrl + '?t=' + Date.now())
    setUploadingAvatar(false)
  }

  // ── Change password ──────────────────────────────────
  async function handleChangePassword() {
    if (newPassword.length < 6) {
      setPasswordMsg({ type: 'err', text: 'Password must be at least 6 characters.' })
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'err', text: 'Passwords do not match.' })
      return
    }
    setSavingPassword(true)
    setPasswordMsg(null)

    const { error } = await supabase.auth.updateUser({ password: newPassword })

    if (error) {
      setPasswordMsg({ type: 'err', text: error.message })
    } else {
      setPasswordMsg({ type: 'ok', text: 'Password updated successfully!' })
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setPasswordMsg(null), 3000)
    }
    setSavingPassword(false)
  }

  // ── RENDER ───────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div style={styles.backdrop} onClick={onClose} />

      {/* Sheet */}
      <div style={styles.sheet}>

        {/* Drag handle */}
        <div style={styles.handle} />

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitle}>My Account</div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Tab bar */}
        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(activeTab === 'profile' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'friends' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('friends')}
          >
            Find Friends
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'password' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('password')}
          >
            Password
          </button>
        </div>

        <div style={styles.body}>

          {/* ══ PROFILE TAB ══ */}
          {activeTab === 'profile' && (
            <>
              {/* Avatar picker — photo or emoji */}
              <div style={styles.avatarSection}>
                <div style={styles.avatarBig}>
                  {avatarUrl
                    ? <img src={avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
                    : avatarEmoji
                  }
                </div>

                <div style={styles.avatarActions}>
                  {/* Hidden file input */}
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleAvatarPhotoChange}
                  />
                  <button
                    style={styles.avatarActionBtn}
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingAvatar}
                  >
                    {uploadingAvatar ? 'Uploading...' : '📷 Upload photo'}
                  </button>
                  <button
                    style={styles.avatarActionBtn}
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    😀 Choose emoji
                  </button>
                  {avatarUrl && (
                    <button
                      style={{ ...styles.avatarActionBtn, color: '#c0392b' }}
                      onClick={() => setAvatarUrl(null)}
                    >
                      ✕ Remove photo
                    </button>
                  )}
                </div>

                {showEmojiPicker && (
                  <div style={styles.emojiGrid}>
                    {EMOJI_OPTIONS.map(e => (
                      <button
                        key={e}
                        style={{
                          ...styles.emojiBtn,
                          background: e === avatarEmoji ? '#e8f5ed' : 'transparent',
                          border: e === avatarEmoji ? '2px solid #2aad56' : '2px solid transparent'
                        }}
                        onClick={() => { setAvatarEmoji(e); setShowEmojiPicker(false) }}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Fields */}
              <div style={styles.fields}>

                <div style={styles.row}>
                  <div style={styles.field}>
                    <label style={styles.label}>Display Name</label>
                    <input
                      style={styles.input}
                      placeholder="Your full name"
                      value={fullName}
                      onChange={e => setFullName(e.target.value)}
                      maxLength={60}
                    />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Username</label>
                    <input
                      style={styles.input}
                      placeholder="username"
                      value={username}
                      onChange={e => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
                      maxLength={30}
                    />
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Bio</label>
                  <textarea
                    style={{ ...styles.input, minHeight: 72, resize: 'none' }}
                    placeholder="Tell people about your game..."
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    maxLength={160}
                  />
                </div>

                <div style={styles.row}>
                  <div style={styles.field}>
                    <label style={styles.label}>Location</label>
                    <input
                      style={styles.input}
                      placeholder="City, Country"
                      value={location}
                      onChange={e => setLocation(e.target.value)}
                      maxLength={80}
                    />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Handicap</label>
                    <input
                      style={styles.input}
                      type="number"
                      placeholder="8.4"
                      step="0.1"
                      min="0"
                      max="54"
                      value={handicap}
                      onChange={e => setHandicap(e.target.value)}
                    />
                  </div>
                </div>

                <div style={styles.row}>
                  <div style={styles.field}>
                    <label style={styles.label}>Birthday</label>
                    <input
                      style={styles.input}
                      type="date"
                      value={birthday}
                      onChange={e => setBirthday(e.target.value)}
                    />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Gender</label>
                    <select
                      style={{ ...styles.input, appearance: 'none', cursor: 'pointer' }}
                      value={gender}
                      onChange={e => setGender(e.target.value)}
                    >
                      {GENDER_OPTIONS.map(g => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                </div>

              </div>

              {profileMsg && (
                <div style={{
                  ...styles.msg,
                  background: profileMsg.type === 'ok' ? '#e8f5ed' : 'rgba(232,64,64,0.1)',
                  color: profileMsg.type === 'ok' ? '#1a7a3c' : '#c0392b',
                  borderColor: profileMsg.type === 'ok' ? '#2aad56' : 'rgba(232,64,64,0.3)'
                }}>
                  {profileMsg.type === 'ok' ? '✓ ' : '⚠ '}{profileMsg.text}
                </div>
              )}

              <button
                style={{ ...styles.saveBtn, opacity: saving ? 0.6 : 1 }}
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </>
          )}

          {/* ══ FIND FRIENDS TAB ══ */}
          {activeTab === 'friends' && (
            <>
              <div style={styles.friendsSearch}>
                <span style={{ fontSize: 16 }}>🔍</span>
                <input
                  style={styles.friendsInput}
                  placeholder="Search by name or username..."
                  value={friendSearch}
                  onChange={e => setFriendSearch(e.target.value)}
                  autoFocus
                />
                {friendSearch.length > 0 && (
                  <button style={styles.friendsClear} onClick={() => { setFriendSearch(''); setFriendResults([]) }}>✕</button>
                )}
              </div>

              {friendSearching && (
                <div style={styles.friendsEmpty}>Searching...</div>
              )}

              {!friendSearching && friendSearch.trim() && friendResults.length === 0 && (
                <div style={styles.friendsEmpty}>No golfers found for "{friendSearch}"</div>
              )}

              {!friendSearch.trim() && (
                <div style={styles.friendsHint}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#0e1f13', marginBottom: 4 }}>Find your golf crew</div>
                  <div style={{ fontSize: 13, color: '#6b7c6e' }}>Search by name or username to find and follow other golfers.</div>
                </div>
              )}

              <div style={styles.friendsList}>
                {friendResults.map(p => {
                  const isFollowing = followingMap[p.id] !== undefined
                    ? followingMap[p.id]
                    : myFollowingIds.has(p.id)
                  return (
                    <div key={p.id} style={styles.friendRow}>
                      <div style={styles.friendAvatar}>
                        {p.avatar_url
                          ? <img src={p.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                          : <span style={{ fontSize: 22 }}>{p.avatar_emoji || '🏌️'}</span>
                        }
                      </div>
                      <div style={styles.friendInfo}>
                        <div style={styles.friendName}>{p.full_name || `@${p.username}`}</div>
                        <div style={styles.friendMeta}>
                          @{p.username}
                          {p.handicap != null && ` · HCP ${p.handicap}`}
                          {p.location && ` · ${p.location}`}
                        </div>
                      </div>
                      <button
                        style={{
                          ...styles.followBtn,
                          ...(isFollowing ? styles.followBtnActive : {})
                        }}
                        onClick={() => isFollowing ? handleUnfollow(p.id) : handleFollow(p.id)}
                      >
                        {isFollowing ? '✓ Following' : '＋ Follow'}
                      </button>
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {/* ══ PASSWORD TAB ══ */}
          {activeTab === 'password' && (
            <>
              <div style={styles.passwordInfo}>
                <div style={styles.passwordIcon}>🔒</div>
                <div style={styles.passwordInfoText}>
                  Choose a strong password with at least 6 characters.
                  You'll stay logged in after changing it.
                </div>
              </div>

              <div style={styles.fields}>
                <div style={styles.field}>
                  <label style={styles.label}>New Password</label>
                  <input
                    style={styles.input}
                    type="password"
                    placeholder="Min 6 characters"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    minLength={6}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Confirm New Password</label>
                  <input
                    style={styles.input}
                    type="password"
                    placeholder="Repeat new password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                  />
                </div>
              </div>

              {passwordMsg && (
                <div style={{
                  ...styles.msg,
                  background: passwordMsg.type === 'ok' ? '#e8f5ed' : 'rgba(232,64,64,0.1)',
                  color: passwordMsg.type === 'ok' ? '#1a7a3c' : '#c0392b',
                  borderColor: passwordMsg.type === 'ok' ? '#2aad56' : 'rgba(232,64,64,0.3)'
                }}>
                  {passwordMsg.type === 'ok' ? '✓ ' : '⚠ '}{passwordMsg.text}
                </div>
              )}

              <button
                style={{
                  ...styles.saveBtn,
                  opacity: (newPassword && confirmPassword && !savingPassword) ? 1 : 0.4
                }}
                onClick={handleChangePassword}
                disabled={!newPassword || !confirmPassword || savingPassword}
              >
                {savingPassword ? 'Updating...' : 'Update Password'}
              </button>

              <div style={styles.divider} />

              <button
                style={styles.logoutBtn}
                onClick={async () => { await supabase.auth.signOut() }}
              >
                Sign Out
              </button>
            </>
          )}

        </div>
      </div>
    </>
  )
}

// ── STYLES ───────────────────────────────────────────────────

const styles = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 200
  },
  sheet: {
    position: 'fixed', bottom: 0, left: 0, right: 0,
    background: 'white',
    borderRadius: '24px 24px 0 0',
    zIndex: 201,
    maxHeight: '92vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 -8px 40px rgba(0,0,0,0.2)'
  },
  handle: {
    width: 40, height: 4, borderRadius: 2,
    background: '#dde5df', margin: '12px auto 0', flexShrink: 0
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px 0', flexShrink: 0
  },
  headerTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 22, color: '#0e1f13', letterSpacing: 0.5
  },
  closeBtn: {
    background: '#f4f7f5', border: 'none', borderRadius: '50%',
    width: 32, height: 32, fontSize: 14, cursor: 'pointer',
    color: '#6b7c6e', display: 'flex', alignItems: 'center', justifyContent: 'center'
  },

  // ── Tabs ─────────────────────────────────────────────
  tabs: {
    display: 'flex', gap: 0, padding: '12px 20px 0', flexShrink: 0,
    borderBottom: '1.5px solid #f0f4f1'
  },
  tab: {
    flex: 1, background: 'none', border: 'none',
    padding: '10px 0', fontSize: 13, fontWeight: 700,
    color: '#6b7c6e', cursor: 'pointer', letterSpacing: 0.3,
    fontFamily: "'Barlow', sans-serif",
    borderBottom: '2px solid transparent', marginBottom: -2
  },
  tabActive: {
    color: '#0e1f13',
    borderBottom: '2px solid #c8f135'
  },

  // ── Scrollable body ───────────────────────────────────
  body: {
    overflowY: 'auto', flex: 1, padding: '20px 20px 40px'
  },

  // ── Avatar ────────────────────────────────────────────
  avatarSection: { textAlign: 'center', marginBottom: 20 },
  avatarBig: {
    width: 80, height: 80, borderRadius: '50%',
    background: 'linear-gradient(135deg, #2aad56, #0e1f13)',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 38, cursor: 'pointer', marginBottom: 6,
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
  },
  avatarActions: { display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 8 },
  avatarActionBtn: {
    background: '#f4f7f5', border: '1px solid #e2ebe4', borderRadius: 20,
    padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
    color: '#0e1f13', fontFamily: "'Barlow', sans-serif"
  },
  emojiGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)',
    gap: 6, marginTop: 12, background: '#f9fbf9',
    borderRadius: 14, padding: 10
  },
  emojiBtn: {
    aspectRatio: '1', fontSize: 20, borderRadius: 8,
    cursor: 'pointer', background: 'transparent', padding: 0
  },

  // ── Fields ────────────────────────────────────────────
  fields: { display: 'flex', flexDirection: 'column', gap: 14 },
  row: { display: 'flex', gap: 10 },
  field: { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 },
  label: {
    fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
    textTransform: 'uppercase', color: '#6b7c6e'
  },
  input: {
    width: '100%', background: '#f9fbf9',
    border: '1.5px solid #e2ebe4', borderRadius: 10,
    padding: '11px 12px', fontSize: 14, color: '#0e1f13',
    outline: 'none', fontFamily: "'Barlow', sans-serif",
    boxSizing: 'border-box'
  },

  // ── Messages ──────────────────────────────────────────
  msg: {
    border: '1px solid', borderRadius: 10,
    padding: '10px 14px', fontSize: 13,
    margin: '14px 0 0', fontWeight: 600
  },

  // ── Buttons ───────────────────────────────────────────
  saveBtn: {
    width: '100%', background: '#c8f135', color: '#0e1f13',
    border: 'none', borderRadius: 14, padding: '16px',
    fontSize: 16, fontWeight: 900, cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1,
    marginTop: 20
  },

  // ── Password tab extras ───────────────────────────────
  passwordInfo: {
    display: 'flex', gap: 12, alignItems: 'flex-start',
    background: '#f4f7f5', borderRadius: 12,
    padding: '14px', marginBottom: 20
  },
  passwordIcon: { fontSize: 24, flexShrink: 0 },
  passwordInfoText: { fontSize: 13, color: '#6b7c6e', lineHeight: 1.5 },

  divider: { height: 1, background: '#f0f4f1', margin: '24px 0' },
  logoutBtn: {
    width: '100%', background: 'none',
    border: '1.5px solid #e2ebe4', borderRadius: 14,
    padding: '14px', fontSize: 14, fontWeight: 700,
    color: '#c0392b', cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif"
  },

  // ── Find Friends ──────────────────────────────────────
  friendsSearch: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: '#f4f7f5', border: '1.5px solid #e2ebe4',
    borderRadius: 14, padding: '10px 14px', marginBottom: 16
  },
  friendsInput: {
    flex: 1, border: 'none', outline: 'none', fontSize: 15,
    color: '#0e1f13', background: 'transparent',
    fontFamily: "'Barlow', sans-serif"
  },
  friendsClear: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#9aaa9d', fontSize: 13, padding: 0
  },
  friendsEmpty: {
    textAlign: 'center', color: '#9aaa9d', fontSize: 13, padding: '24px 0'
  },
  friendsHint: {
    textAlign: 'center', padding: '32px 20px'
  },
  friendsList: { display: 'flex', flexDirection: 'column', gap: 0 },
  friendRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 0', borderBottom: '1px solid #f0f4f1'
  },
  friendAvatar: {
    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#0e1f13)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden'
  },
  friendInfo: { flex: 1, minWidth: 0 },
  friendName: { fontWeight: 700, fontSize: 14, color: '#0e1f13', marginBottom: 2 },
  friendMeta: {
    fontSize: 11, color: '#9aaa9d',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },
  followBtn: {
    flexShrink: 0, background: '#e8f5ed', color: '#1a7a3c',
    border: '1.5px solid #2aad56', borderRadius: 20,
    padding: '6px 14px', fontSize: 12, fontWeight: 800,
    cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif",
    letterSpacing: 0.5
  },
  followBtnActive: {
    background: '#f4f7f5', color: '#6b7c6e',
    border: '1.5px solid #e2ebe4'
  }
}
